import { createRoute } from '@tanstack/react-router';
import { Gauge, Layers, Settings, ShieldCheck } from 'lucide-react';
import { PageHeader } from '@verydia/ui';
import { rootRoute } from './root.tsx';
import { PlaceholderPage } from '../components/placeholder-page.tsx';
import { CustomersPanel } from '../components/customers.tsx';
import { useVendorBreadcrumbs } from '../lib/breadcrumbs.ts';
import { useConsoleContext } from '../lib/console-context.tsx';

/**
 * Resource and platform pages. Each is structurally complete — breadcrumbs, header,
 * card, empty state — and each states plainly which stage brings it to life, so the
 * console reads as an early stage of a real product rather than as a demo (§29).
 */

export const customersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers',
  component: CustomersPage,
});

/**
 * Customers — organization-scoped, and shown that way.
 *
 * The context bar may hold a product while this page is open, and the list is
 * still every customer of the organization. A customer buys several of a
 * vendor's products; filtering by the selected one would invent a relationship
 * the model does not have.
 */
function CustomersPage() {
  const context = useConsoleContext();

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Customers"
        description="The companies that buy your software. A customer holds licences — it is not a Verydia Control user, and not an end user of your application."
        breadcrumbs={[
          { label: context.organization?.name ?? 'Organization', href: '/' },
          { label: 'Customers' },
        ]}
      />
      <CustomersPanel organizationId={context.organization?.id ?? null} />
    </div>
  );
}

export const licensesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/licenses',
  component: () => (
    <PlaceholderPage
      title="Licenses"
      description="Commercial grants: which customer, on which plan, with which seat and activation limits, until when."
      breadcrumbs={useVendorBreadcrumbs({ label: 'Licenses' })}
      icon={<ShieldCheck />}
      emptyTitle="No licenses yet"
      emptyDescription="A license records the commercial terms of a sale. It never carries product feature flags — your application resolves those from the plan code it receives."
      availability="This capability is not available yet."
    />
  ),
});

export const usageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/usage',
  component: () => (
    <PlaceholderPage
      title="Usage"
      description="Metered consumption reported by your software against a license: API calls, documents processed, or any counter you define."
      breadcrumbs={useVendorBreadcrumbs({ label: 'Usage' })}
      icon={<Gauge />}
      emptyTitle="No usage recorded"
      emptyDescription="Usage meters are defined per product and ingested at least once, deduplicated by an idempotency key so a client retry never inflates a total."
      availability="This capability is not available yet."
    />
  ),
});

export const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analytics',
  component: () => (
    <PlaceholderPage
      title="Analytics"
      description="Licensing health across your portfolio: activation rates, expiring licenses, validation volume and seat utilisation."
      breadcrumbs={useVendorBreadcrumbs({ label: 'Analytics' })}
      icon={<Layers />}
      emptyTitle="Nothing to analyse yet"
      emptyDescription="Analytics are derived from licenses, activations and usage events. Charts appear here once those resources exist and have accumulated history."
      availability="This capability is not available yet."
    />
  ),
});

export const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: () => (
    <PlaceholderPage
      title="Settings"
      description="Organization profile, members and roles, and platform preferences."
      breadcrumbs={useVendorBreadcrumbs({ label: 'Settings' })}
      icon={<Settings />}
      emptyTitle="Nothing configurable yet"
      emptyDescription="Organization settings, member invitations and role assignments arrive with the identity foundation."
      availability="This capability is not available yet."
    />
  ),
});
